import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getStaff,
  createStaff,
  updateStaff,
  getStaffHours,
  updateStaffHours,
  getBlockedTimes,
  createBlockedTime,
  deleteBlockedTime,
} from "../lib/api.ts";
import { CloseIcon, PlusIcon } from "../components/Icon.tsx";

interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
}

interface WorkingHours {
  day_of_week: number; // 0=Sun, 6=Sat
  is_working: boolean;
  start_time: string; // "HH:MM"
  end_time: string;
}

interface BlockedTime {
  id: string;
  start_at: string;
  end_at: string;
  reason: string;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const defaultHours = (): WorkingHours[] =>
  DAY_NAMES.map((_, i) => ({
    day_of_week: i,
    is_working: i >= 1 && i <= 5, // Mon-Fri
    start_time: "09:00",
    end_time: "17:00",
  }));

function StaffModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: Partial<StaffMember>;
  onSave: (data: Omit<StaffMember, "id">) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    email: initial?.email ?? "",
    phone: initial?.phone ?? "",
    is_active: initial?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center"
      style={{ background: "rgba(0,0,0,0.55)" }}
    >
      <div
        className="w-full md:max-w-md p-6 md:p-7 rounded-t-2xl md:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{
          background: "var(--paper-raised)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div className="flex items-center justify-between mb-5">
          <h2
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: "-0.012em",
              color: "var(--ink)",
            }}
          >
            {initial?.id ? "Edit staff" : "New staff"}
          </h2>
          <button onClick={onClose} className="zd-icon-btn" aria-label="Close">
            <CloseIcon size={16} />
          </button>
        </div>
        {error && <div className="alert-error mb-4">{error}</div>}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input required className="input" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="staff_active"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="staff_active" className="text-sm" style={{ color: "var(--text)" }}>Active</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HoursEditor({ slug, staffId }: { slug: string; staffId: string }) {
  const [hours, setHours] = useState<WorkingHours[]>(defaultHours());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getStaffHours(slug, staffId)
      .then((rows: Array<{ day_of_week: number; start_time: string; end_time: string; is_active?: number }>) => {
        // Merge API rows into a full 7-day grid so every day shows up in the editor.
        const merged = defaultHours().map((def) => {
          const row = rows?.find?.((r) => r.day_of_week === def.day_of_week);
          if (!row) return { ...def, is_working: false };
          return {
            day_of_week: def.day_of_week,
            is_working: row.is_active === undefined ? true : row.is_active === 1,
            start_time: row.start_time ?? def.start_time,
            end_time: row.end_time ?? def.end_time,
          };
        });
        setHours(merged);
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setLoading(false));
  }, [slug, staffId]);

  const toggle = (i: number) =>
    setHours((h) => h.map((d, idx) => idx === i ? { ...d, is_working: !d.is_working } : d));

  const setTime = (i: number, field: "start_time" | "end_time", val: string) =>
    setHours((h) => h.map((d, idx) => idx === i ? { ...d, [field]: val } : d));

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateStaffHours(slug, staffId, hours);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ fontSize: 13, color: "var(--ink-faint)", padding: "8px 0" }}>Loading hours…</div>;

  return (
    <div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10.5,
          fontWeight: 500,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--ink-faint)",
          marginBottom: 10,
        }}
      >
        Working hours
      </div>
      {error && <div className="alert-error" style={{ fontSize: 12.5, marginBottom: 8 }}>{error}</div>}
      {success && <div className="alert-success" style={{ fontSize: 12.5, marginBottom: 8 }}>Saved.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {hours.map((h, i) => (
          <div key={h.day_of_week} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={h.is_working}
              onChange={() => toggle(i)}
              style={{ width: 14, height: 14, accentColor: "var(--ink)" }}
            />
            <span style={{ width: 36, fontSize: 12, fontWeight: 500, color: "var(--ink-soft)", letterSpacing: "0.04em" }}>
              {DAY_NAMES[h.day_of_week]}
            </span>
            {h.is_working ? (
              <>
                <input
                  type="time"
                  value={h.start_time}
                  onChange={(e) => setTime(i, "start_time", e.target.value)}
                  className="input"
                  style={{ width: 110, padding: "6px 10px", fontSize: 13, fontFeatureSettings: "'tnum'" }}
                />
                <span style={{ color: "var(--ink-faint)" }}>–</span>
                <input
                  type="time"
                  value={h.end_time}
                  onChange={(e) => setTime(i, "end_time", e.target.value)}
                  className="input"
                  style={{ width: 110, padding: "6px 10px", fontSize: 13, fontFeatureSettings: "'tnum'" }}
                />
              </>
            ) : (
              <span style={{ fontSize: 12.5, color: "var(--ink-faint)", fontStyle: "italic", fontFamily: "'Source Serif 4', Georgia, serif" }}>
                Off
              </span>
            )}
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="zd-ghost-btn primary"
        style={{ marginTop: 14, fontSize: 12.5 }}
      >
        {saving ? "Saving…" : "Save hours"}
      </button>
    </div>
  );
}

function addHours(isoLocal: string, hours: number): string {
  // isoLocal is "YYYY-MM-DDTHH:mm" (datetime-local). Parse, add hours, format back.
  if (!isoLocal) return "";
  const d = new Date(isoLocal);
  if (isNaN(d.getTime())) return "";
  d.setTime(d.getTime() + hours * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function BlockedTimesList({ slug, staffId }: { slug: string; staffId: string }) {
  const [items, setItems] = useState<BlockedTime[]>([]);
  const [loading, setLoading] = useState(true);
  const [addForm, setAddForm] = useState({ start_at: "", end_at: "", reason: "" });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    return getBlockedTimes(slug, staffId)
      .then((data: BlockedTime[]) => setItems(data ?? []))
      .catch(() => { /* ignore */ });
  }, [slug, staffId]);

  useEffect(() => {
    refetch().finally(() => setLoading(false));
  }, [refetch]);

  const setStart = (v: string) => {
    setAddForm((f) => ({
      ...f,
      start_at: v,
      // Auto-fill End = Start + 1h if End is empty or stale
      end_at: f.end_at && f.end_at > v ? f.end_at : addHours(v, 1),
    }));
  };

  const addBlocked = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.start_at) return setError("Start time is required");
    const end = addForm.end_at || addHours(addForm.start_at, 1);
    if (end <= addForm.start_at) return setError("End must be after Start");

    setAdding(true);
    setError(null);
    try {
      await createBlockedTime(slug, staffId, {
        start_at: new Date(addForm.start_at).toISOString(),
        end_at: new Date(end).toISOString(),
        reason: addForm.reason || undefined,
      });
      setAddForm({ start_at: "", end_at: "", reason: "" });
      await refetch();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setAdding(false);
    }
  };

  const removeBlocked = async (id: string) => {
    await deleteBlockedTime(slug, staffId, id);
    setItems((prev) => prev.filter((b) => b.id !== id));
  };

  if (loading) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10.5,
          fontWeight: 500,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--ink-faint)",
          marginBottom: 10,
        }}
      >
        Blocked time
      </div>
      {error && <div className="alert-error" style={{ fontSize: 12.5, marginBottom: 8 }}>{error}</div>}
      {items.length > 0 && (
        <ul style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12, listStyle: "none", padding: 0 }}>
          {items.map((b) => (
            <li
              key={b.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: 12.5,
                padding: "8px 12px",
                borderRadius: 8,
                background: "var(--paper-well)",
                color: "var(--ink-soft)",
              }}
            >
              <span style={{ fontFeatureSettings: "'tnum'" }}>
                {new Date(b.start_at).toLocaleString()} <span style={{ color: "var(--ink-faint)" }}>–</span>{" "}
                {new Date(b.end_at).toLocaleString()}
                {b.reason && (
                  <span style={{ color: "var(--ink-faint)" }}> · </span>
                )}
                {b.reason && (
                  <span style={{ fontStyle: "italic", fontFamily: "'Source Serif 4', Georgia, serif" }}>{b.reason}</span>
                )}
              </span>
              <button
                onClick={() => removeBlocked(b.id)}
                aria-label="Remove block"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--ink-faint)",
                  cursor: "pointer",
                  padding: 4,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <CloseIcon size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={addBlocked} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>Start</label>
          <input
            required
            type="datetime-local"
            className="input"
            style={{ padding: "6px 10px", fontSize: 13, fontFeatureSettings: "'tnum'" }}
            value={addForm.start_at}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>End</label>
          <input
            type="datetime-local"
            className="input"
            style={{ padding: "6px 10px", fontSize: 13, fontFeatureSettings: "'tnum'" }}
            value={addForm.end_at}
            onChange={(e) => setAddForm((f) => ({ ...f, end_at: e.target.value }))}
          />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label className="label" style={{ marginBottom: 4 }}>Reason</label>
          <input
            className="input"
            style={{ padding: "6px 10px", fontSize: 13 }}
            placeholder="optional (vacation, training…)"
            value={addForm.reason}
            onChange={(e) => setAddForm((f) => ({ ...f, reason: e.target.value }))}
          />
        </div>
        <button type="submit" disabled={adding} className="zd-ghost-btn" style={{ height: 34 }}>
          {adding ? "Adding…" : "Add block"}
        </button>
      </form>
    </div>
  );
}

export default function Staff() {
  const { slug } = useParams<{ slug: string }>();
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ open: boolean; staff?: StaffMember }>({ open: false });
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    getStaff(slug)
      .then((data: StaffMember[]) => setStaffList(data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data: Omit<StaffMember, "id">) => {
    if (!slug) return;
    if (modal.staff) {
      const updated = await updateStaff(slug, modal.staff.id, data) as StaffMember;
      setStaffList((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } else {
      const created = await createStaff(slug, data) as StaffMember;
      setStaffList((prev) => [...prev, created]);
    }
  };

  return (
    <div>
      <section className="zd-hero" style={{ marginBottom: 32 }}>
        <div className="zd-hero-text">
          <div className="zd-hero-kicker">
            Team <span className="dot">·</span> {staffList.length} {staffList.length === 1 ? "person" : "people"}
          </div>
          <h1 className="zd-hero-headline">Staff</h1>
          <p className="zd-hero-lead">Who works here, when they work, and when they're not available. Click Schedule to set hours and block off time.</p>
        </div>
        <button onClick={() => setModal({ open: true })} className="zd-hero-cta">
          <PlusIcon /> New staff
        </button>
      </section>

      {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ink-faint)", fontSize: 14 }}>
          Loading…
        </div>
      ) : staffList.length === 0 ? (
        <div className="zd-empty">
          <h3>No one on staff yet.</h3>
          <p>Add the people customers can book with. Even a one-person business needs at least one staff record so the schedule can attach to someone.</p>
          <div className="cta-row">
            <button onClick={() => setModal({ open: true })} className="zd-hero-cta">
              <PlusIcon /> New staff
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {staffList.map((s) => {
            const initials = s.name
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((p) => p[0]?.toUpperCase() ?? "")
              .join("");
            const isOpen = expanded === s.id;
            return (
              <article
                key={s.id}
                style={{
                  background: "var(--paper-raised)",
                  borderRadius: 14,
                  padding: 18,
                  boxShadow: "var(--shadow-sm)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: "var(--accent-soft)",
                      color: "var(--accent-ink)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 600,
                      border: "1px solid var(--rule)",
                      flexShrink: 0,
                    }}
                  >
                    {initials || "?"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, fontSize: 14.5, color: "var(--ink)" }}>{s.name}</span>
                      <span className={s.is_active ? "badge-green" : "badge-gray"}>
                        {s.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: "var(--ink-soft)", marginTop: 2 }}>
                      {[s.email, s.phone].filter(Boolean).join(" · ")}
                      {!s.email && !s.phone && (
                        <span style={{ color: "var(--ink-faint)" }}>No contact info on file</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "inline-flex", gap: 6 }}>
                    <button
                      onClick={() => setExpanded(isOpen ? null : s.id)}
                      className="zd-ghost-btn"
                      aria-pressed={isOpen}
                    >
                      {isOpen ? "Hide" : "Schedule"}
                    </button>
                    <button
                      onClick={() => setModal({ open: true, staff: s })}
                      className="zd-ghost-btn"
                    >
                      Edit
                    </button>
                  </div>
                </div>

                {isOpen && slug && (
                  <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--rule)" }}>
                    <HoursEditor slug={slug} staffId={s.id} />
                    <BlockedTimesList slug={slug} staffId={s.id} />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {modal.open && (
        <StaffModal
          initial={modal.staff}
          onSave={handleSave}
          onClose={() => setModal({ open: false })}
        />
      )}
    </div>
  );
}
